import { Button, Datepicker, WeekStart } from 'flowbite-react';
import { DateTime } from 'luxon';
import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { HiOutlineChevronDoubleLeft, HiOutlineChevronDoubleRight } from 'react-icons/hi';

interface MultiDatePickerProps {
    language: string,
    initial: DateTime<true>,
    dates: Array<DateTime<true>>
    setDates: Dispatch<SetStateAction<Array<DateTime<true>>>>
    onWeekChange: (date: DateTime<true>) => void,
}

export const MultiDatePicker = (
    {
        language,
        initial,
        dates,
        setDates,
        onWeekChange
    } : MultiDatePickerProps) => {

    const [date, setDate] = useState<DateTime<true>>(initial)

    const doSetDate = useCallback((date: DateTime<true>) => {
        setDate(date)
        onWeekChange(date)
    }, [onWeekChange])

    const previous = useCallback(() => {
        doSetDate(date?.minus({week: 1}))
    }, [date, doSetDate])

    const next = useCallback(() => {
        doSetDate(date?.plus({week: 1}))
    }, [date, doSetDate])

    const jumpToDate = useCallback((selected: Date | null) => {
        if (selected != null) {
            const date = DateTime.fromJSDate(selected)

            if (date.isValid) {
                doSetDate(date as DateTime<true>)
            }
        }
    }, [doSetDate])

    const weekdays = useCallback((date: DateTime<true>) => {
        const startOfWeek = date.startOf('week')
        date.set({weekday : WeekStart.Monday}) // set to Monday as first

        return Array.from(Array(7).keys())
            .map((val) => {
                return startOfWeek.plus({day: val})
            })
    }, [])

    const isDateSelected = useCallback((date: DateTime<true>) => {
        return dates.some(d => d.equals(date))
    }, [dates])

    const selectDate = useCallback((selected: DateTime<true>) => {
        let newSelectedDates: DateTime<true>[];

        if (isDateSelected(selected)) {
            // Remove the date if already selected
            newSelectedDates = dates.filter((d) => !selected.equals(d))
        } else {
            // Add the date if not selected
            newSelectedDates = [...dates, selected];
        }

        newSelectedDates.sort( (a, b) => a < b ? -1 : a > b ? 1 : 0)

        setDates(newSelectedDates)
    }, [dates, isDateSelected, setDates])

    return (
        <div>
            <div className={"flex flex-row pb-1 items-end"}>
                <Button onClick={previous} color="clear">
                    <HiOutlineChevronDoubleLeft/>
                </Button>
                <div className={"flex flex-col w-full"}>
                    <Datepicker onChange={jumpToDate}
                                language={language}
                                className={"w-full"}
                                weekStart={WeekStart.Monday}
                                value={date.toJSDate()}/>
                </div>
                <Button onClick={next} color="clear">
                    <HiOutlineChevronDoubleRight/>
                </Button>
            </div>
            <div className={"flex flex-row justify-center"}>
                {
                    weekdays(date).map((day, index) => {

                        return (
                            <Button outline={!isDateSelected(day)}
                                    color={"primary"}
                                    onClick={() => selectDate(day)}
                                    key={day.toISODate()}
                                    className={index >= 5 ? "hidden sm:flex" : ""}
                            >
                                {day.weekdayShort}
                            </Button>
                        )
                    })
                }
            </div>
        </div>
    )
}